import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { TokenRefreshStrategy } from './index';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
    private readonly refreshTokenSubject = new Subject<string | undefined>();
    private isRefreshingToken = false;

    constructor(
        @Inject('REFRESH_STRATEGIES')
        private readonly strategies: TokenRefreshStrategy[]
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Block requests from executing while a token is resolving (except for request important to login)
        if (this.isRefreshingToken && !req.url.match(/token|login|portal-api/)) {
            return this.waitForRefreshAndContinue(req, next);
        }

        return next.handle(req).pipe(
            catchError(error => {
                if (error.status === 401) {
                    return this.handle401(req, next);
                }

                return throwError(error);
            })
        );
    }

    private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;
            return this.tryRefreshChain(req, next, 0);
        }
        return this.waitForRefreshAndContinue(req, next);
    }

    private tryRefreshChain(req: HttpRequest<any>, next: HttpHandler, index: number): Observable<HttpEvent<any>> {
        if (index >= this.strategies.length) {
            return throwError(() => new Error('All refresh strategies failed'));
        }
        const strategy: TokenRefreshStrategy = this.strategies[index];

        const tokenRefreshStream$: Observable<HttpEvent<any>> = strategy
            .refreshToken(req, next, this.refreshTokenSubject)
            .pipe(
                catchError(() => {
                    return this.tryRefreshChain(req, next, index + 1);
                }),
                tap(() => {
                    this.isRefreshingToken = false;
                }),
                shareReplay(1)
            );

        // Make sure the stream resolves at least once so other requests aren't stuck waiting for the token refresh
        tokenRefreshStream$.pipe(take(1)).subscribe();

        return tokenRefreshStream$;
    }

    private waitForRefreshAndContinue(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.refreshTokenSubject.pipe(
            filter((refreshToken: string | undefined) => refreshToken !== undefined),
            take(1),
            switchMap(() => next.handle(req))
        );
    }
}
